// src/screens/Marketplace.js
import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Marketplace.css';
import api from '../../api';
import { useCart } from '../../CartContext';
import { useMarketContext } from '../../context/MarketContext';
import UniversalDrawerPopup from '../../components/popups/UniversalDrawerPopup';
import SkeletonCard from '../../components/SkeletonCard';
import { useLikedProducts } from '../../context/LikedProductsContext';
import { useActiveStores } from '../../context/ActiveStoresContext';
import FilterTabs from '../../components/FilterTabs';
import '../../components/FilterTabs.css';
import BannerRow from '../../components/BannerRow';
import EscrowBanner from '../../components/EscrowBanner';
import { useBanners } from '../../context/BannerContext';

// Departments row
import DepartmentRow from '../../components/DepartmentRow';

// ✅ Keep only Suggested rows (imports)
import { SuggestedProvider } from '../../context/SuggestedContext';
import SuggestedRow from '../../components/SuggestedRow';

import { useTheme } from '../../context/ThemeContext';

// Assets
import logo from '../../assets/barterfi-logo.png';
import cartIcon from '../../assets/cart-icon.png';
import homeIcon from '../../assets/home-icon.png';
import notificationsIcon from '../../assets/notifications-icon.png';
import sellIcon from '../../assets/sell-icon.png';
import ordersIcon from '../../assets/orders-icon.png';
import mystoreIcon from '../../assets/mystore-icon.png';
import mywalletIcon from '../../assets/mywallet-icon.png';
import profileIcon from '../../assets/profile-icon.png';
import likeIcon from '../../assets/like-icon.png';
import likeIconRed from '../../assets/like-icon-red.png';
import dotsIcon from '../../assets/dots-icon.png';
import saveIconFilled from '../../assets/save-icon-filled.png';
import placeholderLogo from '../../assets/placeholder-logo.png';
import infoIcon from '../../assets/info-icon.png';
import scrollToTopIcon from '../../assets/scrolltotop-icon.png';
import reloadIcon from '../../assets/reload-icon.png';
import RouteSlider from '../../components/RouteSlider';
import '../../components/RouteSlider.css';

// Sale mid-grid banner
import SaleMidBanner from '../../components/banners/SaleMidBanner';

import homedarkIcon from '../../assets/homedark-icon.png';
import cartdarkIcon from '../../assets/cartdark-icon.png';
import selldarkIcon from '../../assets/selldark-icon.png';
import mystoredarkIcon from '../../assets/mystoredark-icon.png';
import ordersdarkIcon from '../../assets/ordersdark-icon.png';
import notificationsdarkIcon from '../../assets/notificationsdark-icon.png';
import tuneIcon from '../../assets/tune-icon.png';
import MarketCategoryDrawer from '../../components/popups/MarketCategoryDrawer';
import MerchantTrialAwarenessPopup from '../../components/popups/MerchantTrialAwarenessPopup';


// --- add near your other helpers ---
const FALLBACK_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400">
    <rect width="100%" height="100%" fill="#F2F2F2"/>
    <text x="50%" y="50%" font-family="Arial" font-size="22" text-anchor="middle" fill="#999">No image</text>
  </svg>`);


const MERCHANT_TRIAL_POPUP_PASS_DAYS = 7;
const MERCHANT_TRIAL_POPUP_PASS_MS =
  MERCHANT_TRIAL_POPUP_PASS_DAYS * 24 * 60 * 60 * 1000;

function firstProductImage(p) {
  const u = p?.media?.[0];
  if (!u) return FALLBACK_IMG;
  if (/placeholder\.com/i.test(u)) return FALLBACK_IMG;
  return u;
}

function Marketplace({ user, cartCount, setCartCount }) {
  const navigate = useNavigate();
  const [marketFilter, setMarketFilter] = useState("All");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const {
    products: listings,
    loadListings,
    loadingProducts,
    hasMoreMarket,
    likedProductIds,
    setLikedProductIds,
  } = useMarketContext();

  const {
    activeStores,
    loading: storesLoading,
    error: storesError,
    refetch: refetchStores
  } = useActiveStores();

  const { likedProducts, toggleLike, setLikedProducts } = useLikedProducts();
  const { clearBannerCache, loadBanners } = useBanners();
  const [showCategoryDrawer, setShowCategoryDrawer] = useState(false);

  useEffect(() => {
    setLikedProducts(likedProductIds);
  }, [likedProductIds, setLikedProducts]);

  const [convertedPrices, setConvertedPrices] = useState({});
  const [piToUsdRate, setPiToUsdRate] = useState(null);
  const { cartItems } = useCart();

  useEffect(() => {
    if (setCartCount) setCartCount(cartItems.length);
  }, [cartItems, setCartCount]);

  const menuRef = useRef();
  const dotsRef = useRef();
  const [showDynamicInfo, setShowDynamicInfo] = useState(false);

  const marketTabs = useMemo(() => ([
    { key: 'All', label: 'All' },
    { key: 'Search', label: 'Search' },
    { key: 'Barter', label: 'Barter' },
    { key: 'Physical Products', label: 'Physical' },
    { key: 'Digital Products', label: 'Digital' },
    { key: 'Services', label: 'Services' },
  ]), []);

  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);

  const [isMerchantLive, setIsMerchantLive] = useState(false);

  const [popupData, setPopupData] = useState({
    visible: false,
    message: '',
    action: null,
    label: ''
  });

  const openPopup = (message, action = null, label = '') => {
    setPopupData({ visible: true, message, action, label });
  };

  const closePopup = () => {
    setPopupData({ ...popupData, visible: false });
  };

  const [showMerchantBanner, setShowMerchantBanner] = useState(false);

  useEffect(() => {
    const flag = localStorage.getItem('showMerchantSuccessBanner');
    if (flag) {
      setShowMerchantBanner(true);
      localStorage.removeItem('showMerchantSuccessBanner');

      const timer = setTimeout(() => {
        setShowMerchantBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (
      user?.userType === 'merchant' &&
      user?.status === 'approved' &&
      user?.isMerchant === true
    ) {
      setIsMerchantLive(true);
    } else {
      setIsMerchantLive(false);
    }
  }, [user]);

  const scrollRef = useRef();
  const headerRef = useRef();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showReloadIcon, setShowReloadIcon] = useState(true);

  const [uiHidden, setUiHidden] = useState(false);
  const lastScrollTopRef = useRef(0);
  const rafLockRef = useRef(false);


const [merchantTrialPopupOpen, setMerchantTrialPopupOpen] = useState(false);
const [merchantTrialPopupLoading, setMerchantTrialPopupLoading] = useState(false);

const getMerchantTrialPopupPassKey = (username) =>
  `bf_merchant_trial_popup_pass_until:${String(username || '').toLowerCase()}`;

const hasMerchantTrialPopupPassCooldown = React.useCallback((username) => {
  try {
    const raw = localStorage.getItem(getMerchantTrialPopupPassKey(username));
    if (!raw) return false;

    const until = Number(raw || 0);
    return until > Date.now();
  } catch {
    return false;
  }
}, []);

const markMerchantTrialPopupPassed = React.useCallback((username) => {
  try {
    const until = Date.now() + MERCHANT_TRIAL_POPUP_PASS_MS;
    localStorage.setItem(getMerchantTrialPopupPassKey(username), String(until));
  } catch {}
}, []);

const clearMerchantTrialPopupPassed = React.useCallback((username) => {
  try {
    localStorage.removeItem(getMerchantTrialPopupPassKey(username));
  } catch {}
}, []);

useEffect(() => {
  if (!user?.username) return;

  let cancelled = false;

  const checkMerchantTrialPopupEligibility = async () => {
    try {
      setMerchantTrialPopupLoading(true);

      if (hasMerchantTrialPopupPassCooldown(user.username)) {
        if (!cancelled) setMerchantTrialPopupOpen(false);
        return;
      }

      const now = new Date();

      const hasActiveMerchantAccess = Boolean(
        user?.isMerchant === true &&
        user?.merchantPaidUntil &&
        new Date(user.merchantPaidUntil) > now
      );

      const shouldOpen =
        user?.status === 'approved' &&
        user?.merchantTrialUsed !== true &&
        !hasActiveMerchantAccess;

      if (cancelled) return;

      if (shouldOpen) {
        setMerchantTrialPopupOpen(true);
      } else {
        setMerchantTrialPopupOpen(false);
        clearMerchantTrialPopupPassed(user.username);
      }
    } catch (err) {
      console.error('❌ Failed to check merchant trial popup eligibility:', err);
      if (!cancelled) setMerchantTrialPopupOpen(false);
    } finally {
      if (!cancelled) setMerchantTrialPopupLoading(false);
    }
  };

  const timer = setTimeout(() => {
    checkMerchantTrialPopupEligibility();
  }, 900);

  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}, [
  user?.username,
  user?.status,
  user?.isMerchant,
  user?.merchantPaidUntil,
  user?.merchantTrialUsed,
  hasMerchantTrialPopupPassCooldown,
  clearMerchantTrialPopupPassed,
]);


  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await api.get(`/notify/unread-count/${user.username}`);
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {
        console.error('❌ Failed to fetch unread count:', err);
      }
    };
    if (user?.username) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.username]);

  useEffect(() => {
    if (listings.length === 0) {
      loadListings(true, user?.username);
    }
  }, [listings.length, user?.username]);

  useEffect(() => {
    listings.forEach(p => {});
  }, [listings]);

  useEffect(() => {
    const scrollArea = scrollRef.current;
    if (!scrollArea) return;

    let scrollTimeout;

    const handleScroll = () => {
      if (rafLockRef.current) return;
      rafLockRef.current = true;

      requestAnimationFrame(() => {
        const rawTop = scrollArea.scrollTop;
        const scrollTop = Math.max(0, rawTop);
        const clientHeight = scrollArea.clientHeight;
        const scrollHeight = scrollArea.scrollHeight;

        const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;

        setShowScrollTop(scrollTop > 300);
        setShowReloadIcon(scrollTop < 100);

        const prevTop = lastScrollTopRef.current || 0;
        const delta = scrollTop - prevTop;

        const THRESHOLD = 10;
        const ALWAYS_SHOW_TOP = 80;

        if (scrollTop < ALWAYS_SHOW_TOP) {
          setUiHidden(false);
        } else if (Math.abs(delta) > THRESHOLD) {
          if (delta > 0) setUiHidden(true);
          else setUiHidden(false);
        }

        lastScrollTopRef.current = scrollTop;

        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (nearBottom && hasMoreMarket && !loadingProducts) {
            loadListings(false, user?.username);
          }
        }, 150);

        rafLockRef.current = false;
      });
    };

    scrollArea.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      clearTimeout(scrollTimeout);
      scrollArea.removeEventListener('scroll', handleScroll);
    };
  }, [loadListings, hasMoreMarket, loadingProducts, user?.username]);

  useEffect(() => {
    const saved = sessionStorage.getItem('bf_market_scrollTop');
    if (saved && scrollRef.current) {
      const y = parseInt(saved, 10) || 0;
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = y;
      });
    }
  }, []);


const handleMerchantTrialPopupPassed = () => {
  markMerchantTrialPopupPassed(user?.username);
  setMerchantTrialPopupOpen(false);
};

const handleMerchantTrialPopupActivated = async () => {
  try {
    setMerchantTrialPopupOpen(false);
    clearMerchantTrialPopupPassed(user?.username);

    localStorage.setItem('showMerchantSuccessBanner', 'true');

    setTimeout(() => {
      window.location.href = '/marketplace';
    }, 600);
  } catch (err) {
    console.error('❌ Failed after merchant trial popup success:', err);
  }
};



  const handleToggleLike = async (productId) => {
    await toggleLike(productId, api, user.username);

    setLikedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleScrollTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (refetchStores) refetchStores();
    sessionStorage.setItem('bf_market_scrollTop', '0');
  };

  const [reloading, setReloading] = useState(false);

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const navIcons = useMemo(() => ({
    home:          isDark ? homedarkIcon          : homeIcon,
    notifications: isDark ? notificationsdarkIcon : notificationsIcon,
    sell:          isDark ? selldarkIcon          : sellIcon,
    myStore:       isDark ? mystoredarkIcon       : mystoreIcon,
    orders:        isDark ? ordersdarkIcon        : ordersIcon,
    cart:          isDark ? cartdarkIcon          : cartIcon,
  }), [isDark]);

  const handleReload = async () => {
    if (reloading) return;
    setReloading(true);

    setTimeout(() => setReloading(false), 2400);

    clearBannerCache();
    loadBanners();

    setConvertedPrices({});
    await loadListings(true, user?.username);
    sessionStorage.setItem('bf_market_scrollTop', '0');

    if (refetchStores) refetchStores();
  };

  useEffect(() => {
    const fetchPiRate = async () => {
      try {
        const res = await api.get('/convert/rate');
        if (res.data?.piToUsd) {
          setPiToUsdRate(res.data.piToUsd);
        }
      } catch (err) {
        console.error('❌ Failed to fetch cached Pi rate:', err.message);
      }
    };

    fetchPiRate();
    const interval = setInterval(fetchPiRate, 600000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchPendingOrders = async () => {
      try {
        const [buyerRes, sellerRes] = await Promise.all([
          api.get(`/escrow/buyer/${user.username}`),
          api.get(`/escrow/seller/${user.username}`)
        ]);
        const allOrders = [...(buyerRes.data || []), ...(sellerRes.data || [])];
        const pendingCount = allOrders.filter(order => order.status === 'pending').length;
        setPendingOrderCount(pendingCount);
      } catch (err) {
        console.error('❌ Failed to fetch pending orders:', err);
      }
    };

    fetchPendingOrders();
    const interval = setInterval(fetchPendingOrders, 60000);
    return () => clearInterval(interval);
  }, [user.username]);

  const filteredListings = listings.filter(item => {
    if (marketFilter === "All" || marketFilter === "Search") return true;
    if (marketFilter === "Barter") return item.category === "barter";
    if (marketFilter === "Physical Products") return item.category === "physical";
    if (marketFilter === "Digital Products") return item.category === "digital";
    if (marketFilter === "Services") return item.category === "service";
    return false;
  });

  const uniqueListings = Array.from(
    new Map(filteredListings.map(item => [item._id, item])).values()
  );

  const storeRowItems = (activeStores || [])
    .slice()
    .sort((a, b) => a.businessName.localeCompare(b.businessName))
    .slice(0, 20);

  const handleOrdersClick = () => navigate('/orders');

  const handleStoreClick = () => {
    if (user.merchantStatus === 'expired') {
      openPopup(
        'Your merchant subscription has expired.',
        () => navigate('/merchant-payment'),
        'Re-new'
      );
      return;
    }

    if (user.userType === 'merchant' && user.status === 'rejected') {
      openPopup('Your merchant application was rejected. Please reapply.', () =>
        navigate('/register-merchant'), 'Register');
      return;
    }

    if (user.status === 'suspended') {
      openPopup('Your merchant account has been suspended.', () =>
        navigate('/appeal'), 'Appeal');
      return;
    }

    if (user.userType === 'merchant' && user.status === 'approved' && !isMerchantLive) {
      openPopup('Please complete your merchant payment to unlock product listing.', () =>
        navigate('/merchant-payment'), 'Pay Now');
      return;
    }

    if (user.userType === 'merchant' && user.isMerchant !== true) {
      openPopup('Your merchant application is still pending approval.');
      return;
    }

    if (user.userType !== 'merchant') {
      openPopup('You must be a merchant to own a store.', () =>
        navigate('/register-merchant'), 'Register');
      return;
    }

    navigate(`/store/${user.businessUsername}`);
  };

  const handleSellClick = () => {
    if (user.merchantStatus === 'expired') {
      openPopup(
        'Your merchant subscription has expired. You cannot list products until you renew.',
        () => navigate('/merchant-payment'),
        'Re-new'
      );
      return;
    }

    if (user.status === 'suspended') {
      openPopup(
        user.userType === 'merchant'
          ? 'Your merchant account has been suspended. You cannot list products.'
          : 'Your account is suspended. You cannot sell.',
        () => navigate('/appeal'),
        'Appeal'
      );
      return;
    }

    if (user.userType === 'merchant' && user.status === 'rejected') {
      openPopup('Your merchant application was rejected. Please reapply.', () =>
        navigate('/register-merchant'), 'Register');
      return;
    }

    if (user.userType === 'merchant' && user.status === 'approved' && !isMerchantLive) {
      openPopup('Please complete your merchant payment to unlock product listing.', () =>
        navigate('/merchant-payment'), 'Pay Now');
      return;
    }

    if (user.userType !== 'merchant' || user.isMerchant !== true) {
      openPopup(
        user.userType === 'merchant'
          ? 'Your merchant application is still pending approval.'
          : 'You must be a merchant to list products.',
        user.userType === 'merchant' ? null : () => navigate('/register-merchant'),
        user.userType === 'merchant' ? '' : 'Register'
      );
      return;
    }

    navigate('/add-product', { state: { fromMarketplace: true } });
  };

  const initialLoading = listings.length === 0 && loadingProducts;
  const isFetchingMore = listings.length > 0 && loadingProducts;

  const onOpenProduct = (p) => {
    const y = scrollRef.current?.scrollTop || 0;
    sessionStorage.setItem('bf_market_scrollTop', String(y));
    navigate(`/product/${p._id}`, { state: { from: 'marketplace' } });
  };

  const basePi = (p, piToUsdRate) => {
    if (p?.pricingType === 'dynamic' && piToUsdRate && p?.usdPrice != null) {
      return +(p.usdPrice / piToUsdRate).toFixed(2);
    }
    return +Number(p?.price || 0).toFixed(2);
  };

  const saleViewFor = (p, piToUsdRate) => {
    const pv = p?.pricingView;
    if (pv?.isOnFlashSale && pv?.basePrice != null && pv?.effectivePrice != null) {
      const original = +Number(pv.basePrice).toFixed(2);
      const discounted = +Number(pv.effectivePrice).toFixed(2);
      const percent = Math.max(0, Math.round((1 - discounted / Math.max(original, 0.000001)) * 100));
      return { original, discounted, percent, endsAt: pv.saleEndsAt || null };
    }

    const original = basePi(p, piToUsdRate);
    const rawSalePrice = p?.pricing?.salePrice ?? p?.salePrice ?? null;
    const rawPercent   = p?.pricing?.salePercentOff ?? p?.salePercentOff ?? null;

    if (rawSalePrice != null) {
      const discounted = +Number(rawSalePrice).toFixed(2);
      const percent = rawPercent != null
        ? +Number(rawPercent)
        : Math.max(0, Math.round((1 - discounted / Math.max(original, 0.000001)) * 100));
      if (discounted < original) return { original, discounted, percent };
    }

    if (rawPercent != null) {
      const percent = +Number(rawPercent);
      if (Number.isFinite(percent) && percent > 0) {
        const discounted = +Number(original * (1 - percent / 100)).toFixed(2);
        return { original, discounted, percent };
      }
    }

    return null;
  };

  return (
    <SuggestedProvider>
      <div className="marketplace-container">
        <div className="marketplace-scroll-content" ref={scrollRef}>
          {showMerchantBanner && (
            <div className="marketplace-banner success">
              Congratulations! You are now a verified BarterFi merchant.
            </div>
          )}

          <div className={`top-header-wrapper market-top-header ${uiHidden ? 'is-hidden' : ''}`} ref={headerRef}>
            <div className="top-nav top-nav-main market-top-main">
              <div className="home-brand market-brand" onClick={() => navigate('/marketplace')}>
                <img src={logo} alt="BarterFi" className="home-brand-icon" />
                <span className="home-brand-text">BarterFi</span>
              </div>

              <div className="market-top-actions">
                <div className="cart-wrapper" onClick={() => navigate('/cart')}>
                  <img
                    src={navIcons.cart}
                    alt="Cart"
                    className="icon-btn"
                  />
                  {cartCount > 0 && (
                    <div className="cart-count-badge">{cartCount}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="top-nav-tabs market-top-tabs">
              <div className="segmented-tabs">
                <button
                  type="button"
                  className="segmented-tab"
                  onClick={() => navigate('/home')}
                >
                  Social
                </button>

                <button
                  type="button"
                  className="segmented-tab active"
                >
                  Marketplace
                </button>

                <RouteSlider
                  containerSelector=".segmented-tabs"
                  activeSelector=".segmented-tab.active"
                  storageKey="bf_top_pill_prev"
                  currentRoute="marketplace"
                />
              </div>
            </div>
          </div>

          <div
            className="market-searchbar"
            onClick={() => {
              const y = scrollRef.current?.scrollTop || 0;
              sessionStorage.setItem('bf_market_scrollTop', String(y));
              navigate('/market-search');
            }}
            role="button"
            aria-label="Open marketplace search"
          >
            <div className="market-searchbar-input" aria-hidden="true">
              Search BarterFi marketplace
            </div>

            <button
              className="market-searchbar-btn"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCategoryDrawer(true);
              }}
              aria-label="Categories"
            >
              <img src={tuneIcon} alt="" className="market-searchbar-icon" />
            </button>
          </div>

          {storeRowItems.length > 0 && (
            <>
              <div className="store-titlebar">
                <h3 className="store-title">Shop by stores</h3>
                <button
                  className="store-viewall"
                  onClick={() => {
                    const y = scrollRef.current?.scrollTop || 0;
                    sessionStorage.setItem('bf_market_scrollTop', String(y));
                    navigate('/marketplace/stores');
                  }}
                  aria-label="View all stores"
                >
                  View All
                </button>
              </div>

              <div className="store-scroll-wrapper">
                <div className="store-scroll-row">
                  {storeRowItems.map((store) => (
                    <div
                      key={store.businessUsername}
                      className="store-card"
                      onClick={() => navigate(`/store/${store.businessUsername}`)}
                    >
                      <img
                        src={store.businessLogo || placeholderLogo}
                        alt={store.businessName}
                        className="store-card-logo"
                      />
                      <div className="store-card-name">
                        {store.businessName.length > 10
                          ? store.businessName.slice(0, 10) + '...'
                          : store.businessName}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="market-escrow-wrap">
            <EscrowBanner />
          </div>

          <DepartmentRow />

          <div className="marketplace-grid">
            {initialLoading ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : (
              <>
                {uniqueListings.map((item, i) => (
                  <React.Fragment key={item._id}>
                    {uniqueListings.length > 4 && i === 4 && (
                      <div className="market-banner-span">
                        <BannerRow />
                      </div>
                    )}

                    <div
                      className="marketplace-card"
                      onClick={() => {
                        const y = scrollRef.current?.scrollTop || 0;
                        sessionStorage.setItem('bf_market_scrollTop', String(y));
                        navigate(`/product/${item._id}`, { state: { from: 'marketplace' } });
                      }}
                    >
                      <div className="market-img-wrap">
                        {(() => {
                          const v = saleViewFor(item, piToUsdRate);
                          return v ? (
                            <span className="sale-pill left" aria-label={`-${v.percent}% off`}>
                              -{v.percent}%
                            </span>
                          ) : null;
                        })()}
                        <img
                          src={item.media?.[0] || FALLBACK_IMG}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = FALLBACK_IMG;
                          }}
                          alt={item.name}
                          className="marketplace-card-image"
                        />
                      </div>

                      <div className="marketplace-card-info">
                        <div className="marketplace-card-title" title={item.name}>
                          {item.name}
                        </div>

                        <div className="marketplace-info-row">
                          <div className="marketplace-card-price">
                            {(() => {
                              const v = saleViewFor(item, piToUsdRate);
                              const value = v ? v.discounted : basePi(item, piToUsdRate);
                              return <span className={`price-new ${v ? 'on-sale' : ''}`}>{value.toFixed(2)} Pi</span>;
                            })()}
                          </div>

                          <div
                            className="marketplace-like-btn market-like-inside"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleLike(item._id);
                            }}
                          >
                            <img
                              id={`like-icon-${item._id}`}
                              src={likedProducts.includes(item._id) ? likeIconRed : likeIcon}
                              alt="Like"
                              className={`marketplace-like-icon ${likedProducts.includes(item._id) ? 'liked-animate' : ''}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {i === 11 && (
                      <div className="market-suggested-span">
                        <SuggestedRow onOpen={onOpenProduct} />
                      </div>
                    )}

                    {i === 19 && (
                      <div className="market-sale-span">
                        <SaleMidBanner />
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {isFetchingMore && (
                  <>
                    <SkeletonCard />
                    <SkeletonCard />
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {showDynamicInfo && (
          <div className="dynamic-popup-backdrop" onClick={() => setShowDynamicInfo(false)}>
            <div className="dynamic-popup-content" onClick={(e) => e.stopPropagation()}>
              <h3>Dynamic Pricing</h3>
              <p>
                This product uses <strong>dynamic pricing</strong>. The Pi amount is based on a fixed USD price set by the merchant, and updates every 10 minutes to reflect the latest Pi-to-USD rate.
              </p>
              <button onClick={() => setShowDynamicInfo(false)}>Close</button>
            </div>
          </div>
        )}

        <div className={`bottom-nav ${uiHidden ? 'is-hidden' : ''}`}>
          <img
            src={navIcons.home}
            alt="Home"
            className="nav-icon"
            onClick={() => navigate('/home')}
          />

          <div
            className="nav-icon-wrapper"
            style={{ position: 'relative' }}
            onClick={() =>
              navigate('/notifications', { state: { from: 'marketplace' }, replace: true })
            }
          >
            <img src={navIcons.notifications} alt="Notifications" className="nav-icon" />
            {unreadCount > 0 && (
              <span className="nav-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </div>

          <img
            src={navIcons.sell}
            alt="Sell"
            className="nav-icon"
            onClick={handleSellClick}
          />

          <img
            src={navIcons.myStore}
            alt="My Store"
            className="nav-icon"
            onClick={handleStoreClick}
          />

          <div
            className="nav-icon-wrapper"
            style={{ position: 'relative' }}
            onClick={handleOrdersClick}
          >
            <img src={navIcons.orders} alt="Orders" className="nav-icon" />
            {pendingOrderCount > 0 && (
              <span className="nav-badge">{pendingOrderCount > 9 ? '9+' : pendingOrderCount}</span>
            )}
          </div>
        </div>

        {showSearchModal && (
          <div className="modal-backdrop" onClick={() => setShowSearchModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                placeholder="Search marketplace..."
                className="marketplace-modal-input"
              />
              <button onClick={() => setShowSearchModal(false)}>Close</button>
            </div>
          </div>
        )}

        {showScrollTop && (
          <img
            src={scrollToTopIcon}
            alt="Scroll to top"
            className={`market-scrolltop-icon visible ${uiHidden ? 'is-hidden' : ''}`}
            onClick={handleScrollTop}
          />
        )}

        {showReloadIcon && (
          <img
            src={reloadIcon}
            alt="Reload"
            className={`market-reload-icon visible ${reloading ? 'spinning' : ''} ${uiHidden ? 'is-hidden' : ''}`}
            onClick={handleReload}
            style={{ pointerEvents: reloading ? 'none' : 'auto', opacity: reloading ? 0.6 : 1 }}
          />
        )}

        <MarketCategoryDrawer
          open={showCategoryDrawer}
          value={marketFilter}
          onClose={() => setShowCategoryDrawer(false)}
          onSelect={(key) => {
            setMarketFilter(key);
            setShowCategoryDrawer(false);

            if (scrollRef.current) {
              scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
            if (refetchStores) refetchStores();
          }}
        />

        <UniversalDrawerPopup
          visible={popupData.visible}
          message={popupData.message}
          onClose={closePopup}
          onAction={popupData.action}
          buttonLabel={popupData.label}
        />

<MerchantTrialAwarenessPopup
  open={merchantTrialPopupOpen && !merchantTrialPopupLoading}
  onPassed={handleMerchantTrialPopupPassed}
  onActivated={handleMerchantTrialPopupActivated}
/>

      </div>
    </SuggestedProvider>
  );
}

export default Marketplace;
